// Task Tracker — replaces the old generic table for the coach's "Task
// Tracker" sidebar tab. Self-fetching (like SyllabusTracker) so it can pull
// the full raw task list (priority/status/subtasks/comments) instead of the
// pre-formatted rows the old table used.
//
// "Urgent" / "Important" are not stored fields on the task — they're derived
// from the existing priority + status fields (completed -> Completed,
// priority "high" -> Urgent, everything else open -> Important) so no
// backend migration of existing tasks was needed for the three filter tabs.
// "Chat" reuses the task's existing comments thread/endpoints.
import React, { useEffect, useState } from 'react';
import {
    ClipboardCheck,
    Plus,
    Pencil,
    Search,
    ChevronDown,
    ChevronRight,
    CheckCircle2,
    AlertTriangle,
    Clock,
    ListChecks,
} from 'lucide-react';
import './TaskTracker.css';
import AddTaskModal from './AddTaskModal';
import TaskChatPanel, { MentionDot, hasUnseenMention, withMentionsSeen } from './TaskChatPanel';
import {
    getTasks,
    updateTask,
    addSubtask,
    updateSubtask,
    deleteSubtask,
    createTask,
    createSportsTask,
    createMusicTask,
} from '../../api';
import showToast from '../../utils/toast';
import SearchableSelect from '../common/SearchableSelect';

const ITEMS_PER_PAGE = 6;

// Presentation per screen. "coach" is the coach dashboard's Task Tracker as
// it always was; "admin" is the admin Tasks page (Assignor label, each
// person's role under their name, "+ N Sub-tasks", 8 rows per page, and
// every Balagruha's tasks in one request when "All Balagruhas" is chosen).
const VARIANTS = {
    coach: {
        pageSize: ITEMS_PER_PAGE,
        assignerLabel: 'Assigner',
        assigneeFilterLabel: 'Coach',
        assigneeFilterAllLabel: 'All Coaches',
        showRoles: false,
        subtaskSummary: false,
        fetchAcrossBalagruhas: false,
    },
    admin: {
        pageSize: 8,
        assignerLabel: 'Assignor',
        assigneeFilterLabel: 'Assignee',
        assigneeFilterAllLabel: 'All Users',
        showRoles: true,
        subtaskSummary: true,
        fetchAcrossBalagruhas: true,
    },
};

// The list API only returns 10 tasks unless asked for more.
const ALL_TASKS_LIMIT = 1000;

// "purchase-manager" -> "Purchase Manager".
const formatRole = (role) =>
    (role || '')
        .split('-')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

const FILTER_TABS = [
    { id: 'all', label: 'All Tasks', icon: ListChecks },
    { id: 'urgent', label: 'Urgent', icon: AlertTriangle, tone: 'urgent' },
    { id: 'important', label: 'Important', icon: Clock, tone: 'important' },
    { id: 'completed', label: 'Completed', icon: CheckCircle2, tone: 'completed' },
];

// A task counts as overdue once its due date's day has fully passed (not
// merely "due today"), and only while it's still open — a completed task
// keeps its Completed classification regardless of its deadline.
const isPastDueDate = (task) => {
    if (!task.deadline) return false;
    if ((task.status || '').toLowerCase() === 'completed') return false;
    const due = new Date(task.deadline);
    if (Number.isNaN(due.getTime())) return false;
    due.setHours(23, 59, 59, 999);
    return due.getTime() < Date.now();
};

const classifyTask = (task) => {
    if ((task.status || '').toLowerCase() === 'completed') return 'completed';
    if ((task.priority || '').toLowerCase() === 'high') return 'urgent';
    if (isPastDueDate(task)) return 'urgent';
    return 'important';
};

const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// createTask (the plain /api/tasks endpoint) actually creates the task via
// the same createTaskV2 service sports/music tasks use, which always
// returns the created task(s) as an array (one per assignee, even though
// we only ever submit one) under `data.task`. The frontend's createTask()
// helper already unwraps that down to the bare array via
// normalizeTaskResponse, so `response` itself is the array here — it was
// previously mistaken for a single task object with `_id` directly on it,
// which meant this always resolved to undefined and sub-tasks entered in
// the Add Task modal were silently never created.
// createSportsTask/createMusicTask instead return the raw axios payload
// unmodified: { success, data: { task: [...] }, message }.
const extractCreatedTaskId = (response) => {
    if (!response) return undefined;
    if (Array.isArray(response)) return response[0]?._id;
    if (response._id) return response._id;
    const task = response.data?.task ?? response.task;
    if (Array.isArray(task)) return task[0]?._id;
    return task?._id;
};

// Dedupe by _id, preserving the first occurrence's order.
const dedupeById = (items) => {
    const seen = new Set();
    return items.filter((item) => {
        const id = item?._id;
        if (!id) return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    });
};

function TaskTracker({
    selectedBalagruhas,
    balagruhas,
    users,
    canCreate,
    canUpdate,
    onTasksRefreshed,
    variant = 'coach',
    // Optional: clicking a task's name opens its details (admin page).
    onOpenDetails,
    // Optional: extra controls shown in the toolbar before the search box.
    toolbarExtras = null,
    // Optional: bump to make the list fetch again (e.g. after a details edit).
    refreshKey = 0,
    // Optional: people offered in the Assignee filter (defaults to `users`,
    // the same list the assignee dropdowns use).
    assigneeFilterUsers,
}) {
    const role = localStorage.getItem('role');
    const config = VARIANTS[variant] || VARIANTS.coach;
    const pageSize = config.pageSize;
    const filterUsers = assigneeFilterUsers || users || [];

    const [tasksRaw, setTasksRaw] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    // "all" = every Balagruha the coach has (existing default behavior,
    // unchanged); a specific id re-scopes the fetch to just that one.
    const [selectedBalagruhaFilter, setSelectedBalagruhaFilter] = useState('all');
    // "all" = every assignee; a specific user id filters by Assignee (not
    // Assigner) — this is a client-side filter over the already-fetched list.
    const [selectedCoachFilter, setSelectedCoachFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedTaskId, setExpandedTaskId] = useState(null);

    const [addSubtaskFor, setAddSubtaskFor] = useState(null);
    const [subtaskTitle, setSubtaskTitle] = useState('');
    const [subtaskDueDate, setSubtaskDueDate] = useState('');
    const [subtaskPriority, setSubtaskPriority] = useState('medium');

    const [chatTask, setChatTask] = useState(null);

    const [showAddTask, setShowAddTask] = useState(false);
    const [editingTask, setEditingTask] = useState(null);

    const fetchTasks = async () => {
        // Balgruha dropdown set to a specific Balagruha scopes the fetch to
        // just that one; "all" keeps the existing default (every Balagruha
        // the coach has, via selectedBalagruhas from the parent).
        const balagruhaIdsToQuery = selectedBalagruhaFilter === 'all'
            ? selectedBalagruhas
            : [selectedBalagruhaFilter];

        // Admin + "All Balagruhas": one request without a Balagruha, which
        // the backend answers with every Balagruha the user may see.
        const everyBalagruha = config.fetchAcrossBalagruhas && selectedBalagruhaFilter === 'all';

        if (!everyBalagruha && (!balagruhaIdsToQuery || balagruhaIdsToQuery.length === 0)) {
            setTasksRaw([]);
            return;
        }
        setLoading(true);
        try {
            // The backend silently caps this list at 10 tasks per Balagruha
            // when no limit is sent — this page does its own pagination
            // (ITEMS_PER_PAGE) over the full set, so ask for effectively
            // everything instead of letting the server truncate it first.
            const responses = everyBalagruha
                ? [await getTasks({ limit: ALL_TASKS_LIMIT })]
                : await Promise.all(
                    balagruhaIdsToQuery.map((balagruhaId) => getTasks({ balagruhaId, limit: 500 }))
                );
            const rawTasks = dedupeById(
                responses.flatMap((response) => response?.data?.tasks || [])
            );
            setTasksRaw(rawTasks);
            onTasksRefreshed?.();
        } catch (error) {
            showToast('Failed to load tasks', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBalagruhas, selectedBalagruhaFilter, refreshKey]);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeFilter, searchText, selectedBalagruhaFilter, selectedCoachFilter]);

    // The backend scopes the fetch by which USERS belong to the selected
    // Balagruha (a proxy), so staff shared across Balagruhas can make that
    // scoping alone look like it did nothing. Each task also carries its own
    // balagruhaId (set by the Add Task form), so narrow once more here using
    // that direct field for an accurate result — tasks with no balagruhaId
    // recorded are left visible rather than hidden, so nothing that used to
    // show up under "All Balagruhas" disappears.
    const matchesBalagruhaFilter = (task) => {
        if (selectedBalagruhaFilter === 'all') return true;
        if (!task.balagruhaId) return true;
        const taskBalagruhaId = typeof task.balagruhaId === 'object'
            ? (task.balagruhaId._id || task.balagruhaId)
            : task.balagruhaId;
        return String(taskBalagruhaId) === String(selectedBalagruhaFilter);
    };

    // Coach dropdown filters by Assignee, not Assigner, and applies before
    // the Urgent/Important/Completed tab counts so the badges stay accurate
    // for whichever assignee/Balagruha combination is selected.
    const coachFilteredTasks = tasksRaw.filter((task) => {
        if (!matchesBalagruhaFilter(task)) return false;
        if (selectedCoachFilter === 'all') return true;
        return (task.assignedUser?._id || task.assignedUser) === selectedCoachFilter;
    });

    const counts = coachFilteredTasks.reduce((acc, task) => {
        const tone = classifyTask(task);
        acc[tone] = (acc[tone] || 0) + 1;
        return acc;
    }, {});

    const searchLower = searchText.trim().toLowerCase();
    const filteredTasks = coachFilteredTasks.filter((task) => {
        if (activeFilter !== 'all' && classifyTask(task) !== activeFilter) return false;
        if (!searchLower) return true;
        const assigneeName = task.assignedUser?.name || '';
        const assignerName = task.createdBy?.name || '';
        return (
            (task.title || '').toLowerCase().includes(searchLower) ||
            assigneeName.toLowerCase().includes(searchLower) ||
            assignerName.toLowerCase().includes(searchLower)
        );
    });

    const totalPages = Math.max(1, Math.ceil(filteredTasks.length / pageSize));
    const pageSafe = Math.min(currentPage, totalPages);
    const paginatedTasks = filteredTasks.slice(
        (pageSafe - 1) * pageSize,
        pageSafe * pageSize
    );

    // A person's role: from the task itself, else from the users list.
    const roleOf = (person) => {
        if (!person) return '';
        if (typeof person === 'object' && person.role) return formatRole(person.role);
        const id = typeof person === 'object' ? person._id : person;
        return formatRole((users || []).find((u) => u._id === id)?.role);
    };

    // Name the selected Balagruha in the empty state instead of a generic
    // "No tasks found", when that's specifically why the list is empty.
    const selectedBalagruhaName = selectedBalagruhaFilter !== 'all'
        ? (balagruhas || []).find((b) => b._id === selectedBalagruhaFilter)?.name
        : null;
    const selectedCoachDisplayName = selectedCoachFilter !== 'all'
        ? filterUsers.find((u) => u._id === selectedCoachFilter)?.name
        : null;
    const emptyStateMessage = (() => {
        if (selectedBalagruhaName && selectedCoachDisplayName) {
            return `${selectedBalagruhaName} has no tasks for ${selectedCoachDisplayName}.`;
        }
        if (selectedBalagruhaName) {
            return `${selectedBalagruhaName} has no tasks.`;
        }
        if (selectedCoachDisplayName) {
            return `No tasks assigned to ${selectedCoachDisplayName}.`;
        }
        return 'No tasks found';
    })();

    const toggleExpand = (taskId) => {
        setExpandedTaskId((prev) => (prev === taskId ? null : taskId));
    };

    const handleAssigneeChange = async (task, newUserId) => {
        const currentId = task.assignedUser?._id || task.assignedUser;
        if (!canUpdate || !newUserId || newUserId === currentId) return;
        try {
            await updateTask(task._id, { assignedUser: newUserId });
            fetchTasks();
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to reassign task', 'error');
        }
    };

    const openAddSubtask = (taskId) => {
        setAddSubtaskFor((prev) => (prev === taskId ? null : taskId));
        setSubtaskTitle('');
        setSubtaskDueDate('');
        setSubtaskPriority('medium');
    };

    const handleAddSubtask = async (taskId) => {
        // Previously a bare `return` here — clicking "Add" with an empty
        // name silently did nothing, which is what made it look like the
        // sub-task "wasn't shown" after adding it.
        if (!subtaskTitle.trim()) {
            showToast('Sub-task name is required', 'error');
            return;
        }
        try {
            await addSubtask(taskId, {
                title: subtaskTitle.trim(),
                dueDate: subtaskDueDate || undefined,
                priority: subtaskPriority,
            });
            setSubtaskTitle('');
            setSubtaskDueDate('');
            setSubtaskPriority('medium');
            setAddSubtaskFor(null);
            setExpandedTaskId(taskId);
            fetchTasks();
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to add sub-task', 'error');
        }
    };

    const handleToggleSubtaskStatus = async (task, subtask) => {
        if (!canUpdate) return;
        try {
            const newStatus = subtask.status === 'completed' ? 'pending' : 'completed';
            await updateSubtask(task._id, subtask._id, { status: newStatus });

            // Keep the parent task's own completion state in sync with its
            // sub-tasks: once every sub-task is done, auto-complete the
            // task; if one gets reopened afterwards, reopen the task too.
            const allSubtasksCompleted = (task.subtasks || []).every((st) => (
                st._id === subtask._id ? newStatus === 'completed' : st.status === 'completed'
            ));
            if (allSubtasksCompleted && task.status !== 'completed') {
                await updateTask(task._id, { status: 'completed' });
            } else if (!allSubtasksCompleted && task.status === 'completed') {
                await updateTask(task._id, { status: 'pending' });
            }

            fetchTasks();
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to update sub-task', 'error');
        }
    };

    const openChat = (task) => setChatTask(task);

    const closeChat = () => setChatTask(null);

    const handleCreateTask = async (taskData) => {
        try {
            const formData = new FormData();
            formData.append('title', taskData.title);
            formData.append('description', taskData.description);
            const assignedUserIds = Array.isArray(taskData.assignedUser)
                ? taskData.assignedUser.join(',')
                : taskData.assignedUser || '';
            formData.append('assignedUser', assignedUserIds);
            formData.append('deadline', taskData.deadline);
            formData.append('priority', taskData.priority);
            formData.append('status', taskData.status);

            if (taskData.balagruhaId) {
                formData.append('balagruhaId', taskData.balagruhaId);
            }

            if (role === 'sports-coach' || role === 'music-coach') {
                formData.append('drillOrExerciseType', taskData.drillOrExerciseType);
            }

            if (taskData.type === 'purchase' || taskData.type === 'order') {
                formData.append('machineDetails', taskData.machineDetails);
                formData.append('vendorDetails', taskData.vendorDetails);
                formData.append('costEstimate', taskData.costEstimate);
                formData.append('type', taskData.type);
            }

            if (taskData.type === 'order') {
                formData.append('repairDetails', taskData.repairDetails);
            } else if (taskData.type === 'purchase') {
                formData.append('requiredParts', taskData.requiredParts);
            }

            if (taskData.type === 'medical') {
                formData.append('type', taskData.type);
                if (taskData.balagruhaId) {
                    formData.append('balagruhaId', taskData.balagruhaId);
                }
                if (taskData.students && taskData.students.length > 0) {
                    taskData.students.forEach((studentId) => {
                        formData.append('students[]', studentId);
                    });
                }
            }

            if (taskData.attachments && taskData.attachments.length > 0) {
                taskData.attachments.forEach((file) => {
                    formData.append('attachments', file);
                });
            }

            let createResponse;
            if (role === 'sports-coach') {
                createResponse = await createSportsTask(formData);
            } else if (role === 'music-coach') {
                createResponse = await createMusicTask(formData);
            } else {
                createResponse = await createTask(formData);
            }

            const createdTaskId = extractCreatedTaskId(createResponse);

            if (createdTaskId && Array.isArray(taskData.subtasks) && taskData.subtasks.length > 0) {
                try {
                    // One at a time, not Promise.all: the backend adds a
                    // sub-task by reading the task's current subtasks array
                    // and writing the whole array back (not an atomic
                    // push), so firing these in parallel is a lost-update
                    // race — each call reads the same pre-update snapshot,
                    // and whichever write lands last overwrites the rest,
                    // leaving only one sub-task behind.
                    for (const subtask of taskData.subtasks) {
                        await addSubtask(createdTaskId, {
                            title: subtask.title,
                            dueDate: subtask.dueDate || undefined,
                            priority: subtask.priority || undefined,
                        });
                    }
                } catch (subtaskError) {
                    showToast('Task created, but some sub-tasks failed to save', 'error');
                }
            }

            showToast(`Task "${taskData.title}" created successfully!`, 'success');
            setShowAddTask(false);
            fetchTasks();
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to create task', 'error');
        }
    };

    const handleUpdateTask = async (taskData) => {
        if (!editingTask) return;
        const { subtaskChanges, ...taskFields } = taskData;
        // The server only lets a task be completed once its *saved*
        // sub-tasks are all completed. When this same save also completes
        // sub-tasks, sending status first gets rejected because those
        // changes aren't stored yet — so hold "completed" back until the
        // sub-task changes below have gone through.
        const deferCompletion = taskFields.status === 'completed' && !!subtaskChanges;
        const { status: _deferredStatus, ...fieldsWithoutStatus } = taskFields;
        try {
            // taskFields is already shaped for the update endpoint:
            // title/description/assignedUser/deadline, plus either
            // priority (Urgent/Important) or status ("Completed", or
            // "pending" again when reopening a completed task).
            await updateTask(editingTask._id, deferCompletion ? fieldsWithoutStatus : taskFields);

            let subtaskSaveFailed = false;
            if (subtaskChanges) {
                const { added = [], updated = [], deletedIds = [] } = subtaskChanges;
                try {
                    // One at a time, not Promise.all: the add/update/delete
                    // subtask endpoints each read-then-replace the whole
                    // subtasks array (not an atomic op), so firing several
                    // at once risks the same lost-update race fixed
                    // earlier for sub-task creation.
                    for (const item of added) {
                        await addSubtask(editingTask._id, item);
                    }
                    for (const item of updated) {
                        await updateSubtask(editingTask._id, item.id, {
                            title: item.title,
                            dueDate: item.dueDate,
                            priority: item.priority,
                            status: item.status,
                        });
                    }
                    for (const id of deletedIds) {
                        await deleteSubtask(editingTask._id, id);
                    }
                } catch (subtaskError) {
                    subtaskSaveFailed = true;
                    showToast('Task updated, but some sub-task changes failed to save', 'error');
                }
            }

            if (deferCompletion && !subtaskSaveFailed) {
                await updateTask(editingTask._id, { status: 'completed' });
            }

            showToast(`Task "${taskFields.title}" updated successfully!`, 'success');
            setEditingTask(null);
            fetchTasks();
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to update task', 'error');
        }
    };

    return (
        <div className="task-tracker">
            <div className="task-tracker-header">
                <div className="task-tracker-title">
                    <span className="task-tracker-title-icon" aria-hidden="true">
                        <ClipboardCheck size={24} color="#f2762e" strokeWidth={2.25} />
                    </span>
                    <div>
                        <h3>Task Tracker</h3>
                        <div className="task-tracker-subtitle">Plan · Assign · Track · Get Things Done</div>
                    </div>
                </div>

                <div className="task-tracker-header-controls">
                    <div className="task-tracker-context-pill">
                        <span className="task-tracker-context-label">Balgruha</span>
                        <SearchableSelect
                            searchPlaceholder="Search balagruha..."
                            className="task-tracker-context-select"
                            value={selectedBalagruhaFilter}
                            onChange={(e) => setSelectedBalagruhaFilter(e.target.value)}
                        >
                            <option value="all">All Balagruhas</option>
                            {(balagruhas || []).map((b) => (
                                <option key={b._id} value={b._id}>{b.name}</option>
                            ))}
                        </SearchableSelect>
                    </div>
                    <div className="task-tracker-context-pill">
                        <span className="task-tracker-context-label">{config.assigneeFilterLabel}</span>
                        <SearchableSelect
                            searchPlaceholder="Search name..."
                            className="task-tracker-context-select"
                            value={selectedCoachFilter}
                            onChange={(e) => setSelectedCoachFilter(e.target.value)}
                        >
                            <option value="all">{config.assigneeFilterAllLabel}</option>
                            {filterUsers.map((u) => (
                                <option key={u._id} value={u._id}>
                                    {u.name}{config.showRoles && u.role ? ` (${formatRole(u.role)})` : ''}
                                </option>
                            ))}
                        </SearchableSelect>
                    </div>
                    {canCreate && (
                        <button
                            type="button"
                            className="task-tracker-add-btn"
                            onClick={() => setShowAddTask(true)}
                        >
                            <Plus size={16} strokeWidth={2.5} /> Add Task
                        </button>
                    )}
                </div>
            </div>

            <div className="task-tracker-toolbar">
                <div className="task-tracker-filter-tabs">
                    {FILTER_TABS.map((tab) => {
                        const TabIcon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                className={`task-tracker-filter-tab tone-${tab.tone || 'all'} ${activeFilter === tab.id ? 'active' : ''}`}
                                onClick={() => setActiveFilter(tab.id)}
                            >
                                <TabIcon size={14} />
                                {tab.label}{tab.id !== 'all' ? ` (${counts[tab.id] || 0})` : ''}
                            </button>
                        );
                    })}
                </div>
                {(() => {
                    const search = (
                        <div className="task-tracker-search">
                            <Search size={16} className="task-tracker-search-icon" aria-hidden="true" />
                            <input
                                type="text"
                                placeholder="Search tasks..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </div>
                    );
                    // Extras sit with the search box on the right; without
                    // extras the toolbar is exactly as before.
                    return toolbarExtras ? (
                        <div className="task-tracker-toolbar-end">
                            {toolbarExtras}
                            {search}
                        </div>
                    ) : search;
                })()}
            </div>

            <div className="task-tracker-card">
                <div className="task-tracker-table-wrapper">
                    <table className="task-tracker-table">
                        <colgroup>
                            <col className="task-tracker-col-srno" />
                            <col className="task-tracker-col-assigner" />
                            <col className="task-tracker-col-assignee" />
                            <col className="task-tracker-col-taskname" />
                            <col className="task-tracker-col-assigned-date" />
                            <col className="task-tracker-col-due-date" />
                            <col className="task-tracker-col-chat" />
                            <col className="task-tracker-col-actions" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>Sr No</th>
                                <th>{config.assignerLabel}</th>
                                <th>Assignee</th>
                                <th>Task Name</th>
                                <th>Assigned Date</th>
                                <th>Due Date</th>
                                <th>Chat</th>
                                <th><span className="sr-only">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={8} className="task-tracker-empty">Loading tasks…</td></tr>
                            )}
                            {!loading && paginatedTasks.length === 0 && (
                                <tr><td colSpan={8} className="task-tracker-empty">{emptyStateMessage}</td></tr>
                            )}
                            {!loading && paginatedTasks.map((task, index) => {
                                const tone = classifyTask(task);
                                const hasSubtasks = Array.isArray(task.subtasks) && task.subtasks.length > 0;
                                const isExpanded = expandedTaskId === task._id;
                                const srNo = (pageSafe - 1) * pageSize + index + 1;
                                const assigneeId = task.assignedUser?._id || task.assignedUser || '';
                                const assigneeName = task.assignedUser?.name
                                    || (users || []).find((u) => u._id === assigneeId)?.name
                                    || 'Unassigned';
                                const assigneeRole = roleOf(task.assignedUser);
                                const assignerRole = roleOf(task.createdBy);
                                const subtaskCount = hasSubtasks ? task.subtasks.length : 0;
                                const lastMessage = task.comments && task.comments.length > 0
                                    ? task.comments[task.comments.length - 1].comment
                                    : (task.description || 'No messages yet');

                                return (
                                    <React.Fragment key={task._id}>
                                        <tr className="task-tracker-row">
                                            <td>{srNo}</td>
                                            <td>
                                                {task.createdBy?.name || 'N/A'}
                                                {config.showRoles && assignerRole && (
                                                    <span className="task-tracker-person-role">({assignerRole})</span>
                                                )}
                                            </td>
                                            <td>
                                                {config.showRoles ? (
                                                    // Name and role in a box; the real select sits on top
                                                    // (transparent) so it stays a native, accessible control.
                                                    <div className={`task-tracker-assignee-picker${canUpdate ? '' : ' is-readonly'}`}>
                                                        <span className="task-tracker-assignee-name">{assigneeName}</span>
                                                        {assigneeRole && (
                                                            <span className="task-tracker-person-role">({assigneeRole})</span>
                                                        )}
                                                        {canUpdate && (
                                                            <>
                                                                <ChevronDown size={16} className="task-tracker-assignee-chevron" aria-hidden="true" />
                                                                <SearchableSelect
                                                                    searchPlaceholder="Search name..."
                                                                    value={assigneeId}
                                                                    onChange={(e) => handleAssigneeChange(task, e.target.value)}
                                                                    className="task-tracker-assignee-select is-overlay"
                                                                    aria-label={`Assignee for ${task.title}`}
                                                                >
                                                                    {!task.assignedUser?._id && <option value="">Unassigned</option>}
                                                                    {(users || []).map((u) => (
                                                                        <option key={u._id} value={u._id}>
                                                                            {u.name}{u.role ? ` (${formatRole(u.role)})` : ''}
                                                                        </option>
                                                                    ))}
                                                                </SearchableSelect>
                                                            </>
                                                        )}
                                                    </div>
                                                ) : canUpdate ? (
                                                    <SearchableSelect
                                                        searchPlaceholder="Search name..."
                                                        value={task.assignedUser?._id || task.assignedUser || ''}
                                                        onChange={(e) => handleAssigneeChange(task, e.target.value)}
                                                        className="task-tracker-assignee-select"
                                                    >
                                                        {!task.assignedUser?._id && <option value="">Unassigned</option>}
                                                        {(users || []).map((u) => (
                                                            <option key={u._id} value={u._id}>{u.name}</option>
                                                        ))}
                                                    </SearchableSelect>
                                                ) : (task.assignedUser?.name || 'Unassigned')}
                                            </td>
                                            {config.subtaskSummary ? (
                                            <td>
                                                <div className="task-tracker-taskname">
                                                    <span className={`task-tracker-dot tone-${tone}`} aria-hidden="true" />
                                                    {onOpenDetails ? (
                                                        <button
                                                            type="button"
                                                            className="task-tracker-taskname-text task-tracker-taskname-link"
                                                            onClick={() => onOpenDetails(task)}
                                                            title="Open task details"
                                                        >
                                                            {task.title}
                                                        </button>
                                                    ) : (
                                                        <span className="task-tracker-taskname-text">{task.title}</span>
                                                    )}
                                                </div>
                                                {hasSubtasks ? (
                                                    <button
                                                        type="button"
                                                        className="task-tracker-subtask-summary"
                                                        onClick={() => toggleExpand(task._id)}
                                                        aria-expanded={isExpanded}
                                                    >
                                                        + {subtaskCount} Sub-task{subtaskCount === 1 ? '' : 's'}
                                                    </button>
                                                ) : canUpdate && (
                                                    <button
                                                        type="button"
                                                        className="task-tracker-add-subtask-link"
                                                        onClick={() => openAddSubtask(task._id)}
                                                    >
                                                        <Plus size={11} /> Sub-task
                                                    </button>
                                                )}
                                            </td>
                                            ) : (
                                            <td>
                                                <div
                                                    className={`task-tracker-taskname ${hasSubtasks ? 'clickable' : ''}`}
                                                    onClick={hasSubtasks ? () => toggleExpand(task._id) : undefined}
                                                >
                                                    <span className={`task-tracker-dot tone-${tone}`} aria-hidden="true" />
                                                    <span className="task-tracker-taskname-text">{task.title}</span>
                                                    {hasSubtasks && (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
                                                </div>
                                                {canUpdate && (
                                                    <button
                                                        type="button"
                                                        className="task-tracker-add-subtask-link"
                                                        onClick={() => openAddSubtask(task._id)}
                                                    >
                                                        <Plus size={11} /> Sub-task
                                                    </button>
                                                )}
                                            </td>
                                            )}
                                            <td>{formatDate(task.createdAt)}</td>
                                            <td>{formatDate(task.deadline)}</td>
                                            <td className="task-tracker-chat-cell">
                                                <div className="task-tracker-chat-cell-row">
                                                    <div className="task-tracker-last-message">{lastMessage}</div>
                                                    <button
                                                        type="button"
                                                        className="task-tracker-view-chat"
                                                        onClick={() => openChat(task)}
                                                    >
                                                        View Chat{task.comments?.length ? ` (${task.comments.length})` : ''}
                                                        {hasUnseenMention(task) && <MentionDot />}
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="task-tracker-actions-cell">
                                                {canUpdate && (
                                                    <button
                                                        type="button"
                                                        className="task-tracker-edit-btn"
                                                        onClick={() => setEditingTask(task)}
                                                        aria-label={`Edit ${task.title}`}
                                                        title="Edit task"
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>

                                        {addSubtaskFor === task._id && (
                                            <tr className="task-tracker-add-subtask-row">
                                                <td colSpan={8}>
                                                    <div className="task-tracker-add-subtask-form">
                                                        <input
                                                            type="text"
                                                            placeholder="Sub-task name"
                                                            value={subtaskTitle}
                                                            onChange={(e) => setSubtaskTitle(e.target.value)}
                                                        />
                                                        <input
                                                            type="date"
                                                            value={subtaskDueDate}
                                                            onChange={(e) => setSubtaskDueDate(e.target.value)}
                                                        />
                                                        <select
                                                            value={subtaskPriority}
                                                            onChange={(e) => setSubtaskPriority(e.target.value)}
                                                            className="task-tracker-add-subtask-priority"
                                                        >
                                                            <option value="high">Urgent</option>
                                                            <option value="medium">Important</option>
                                                        </select>
                                                        <button type="button" onClick={() => handleAddSubtask(task._id)}>Add</button>
                                                        <button type="button" className="ghost" onClick={() => setAddSubtaskFor(null)}>Cancel</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}

                                        {hasSubtasks && isExpanded && (
                                            <tr className="task-tracker-subtasks-row">
                                                <td colSpan={8}>
                                                    <div className="task-tracker-subtasks">
                                                        <div className="task-tracker-subtasks-title">
                                                            Sub-Tasks ({task.subtasks.length})
                                                            {config.subtaskSummary && canUpdate && (
                                                                <button
                                                                    type="button"
                                                                    className="task-tracker-add-subtask-link"
                                                                    onClick={() => openAddSubtask(task._id)}
                                                                >
                                                                    <Plus size={11} /> Add sub-task
                                                                </button>
                                                            )}
                                                        </div>
                                                        <table className="task-tracker-subtasks-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Sr No</th>
                                                                    <th>Sub-Task Name</th>
                                                                    <th>Status</th>
                                                                    <th>Due Date</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {task.subtasks.map((subtask, subIndex) => (
                                                                    <tr key={subtask._id}>
                                                                        <td>{subIndex + 1}</td>
                                                                        <td>{subtask.title}</td>
                                                                        <td>
                                                                            <button
                                                                                type="button"
                                                                                className={`task-tracker-subtask-status ${subtask.status === 'completed' ? 'completed' : 'important'}`}
                                                                                onClick={() => handleToggleSubtaskStatus(task, subtask)}
                                                                                disabled={!canUpdate}
                                                                            >
                                                                                {subtask.status === 'completed' ? (
                                                                                    <><CheckCircle2 size={13} /> Completed</>
                                                                                ) : (
                                                                                    <><Clock size={13} /> Important</>
                                                                                )}
                                                                            </button>
                                                                        </td>
                                                                        <td>{formatDate(subtask.dueDate)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="task-tracker-footer">
                    <div className="task-tracker-footer-info">
                        {filteredTasks.length === 0
                            ? 'Showing 0 tasks'
                            : `Showing ${(pageSafe - 1) * pageSize + 1} to ${Math.min(pageSafe * pageSize, filteredTasks.length)} of ${filteredTasks.length} tasks`}
                    </div>
                    {totalPages > 1 && (
                        <div className="task-tracker-pagination">
                            <button disabled={pageSafe === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>‹</button>
                            {Array.from({ length: totalPages }).map((_, i) => (
                                <button
                                    key={i}
                                    className={pageSafe === i + 1 ? 'active' : ''}
                                    onClick={() => setCurrentPage(i + 1)}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button disabled={pageSafe === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>›</button>
                        </div>
                    )}
                </div>
            </div>

            {chatTask && (
                <TaskChatPanel
                    task={chatTask}
                    onClose={closeChat}
                    onMessageSent={fetchTasks}
                    onMentionsSeen={(taskId) => setTasksRaw((list) => withMentionsSeen(list, taskId))}
                />
            )}

            {showAddTask && (
                <AddTaskModal
                    users={users}
                    balagruhas={balagruhas}
                    defaultBalagruhaId={selectedBalagruhas?.[0] || ''}
                    onSubmit={handleCreateTask}
                    onCancel={() => setShowAddTask(false)}
                />
            )}

            {editingTask && (
                <AddTaskModal
                    mode="edit"
                    initialTask={editingTask}
                    users={users}
                    balagruhas={balagruhas}
                    onSubmit={handleUpdateTask}
                    onCancel={() => setEditingTask(null)}
                />
            )}
        </div>
    );
}

export default TaskTracker;
